import type { Storage } from '../platform/storage'

/**
 * Исходящая корзина обмена: что с устройства ещё не уехало на сервер.
 *
 * Раньше посылка была всей историей малыша целиком и росла с каждым днём — 26.10.2026 она
 * перевалила за лимит nginx, и обмен встал. Теперь устройство само помечает, что изменилось:
 * новая запись журнала, удаление старой, новое начало окна — и в посылку идёт только это
 * (решение заказчика 27.10.2026). Заметки едут по своей метке изменения. После удачного
 * обмена метки снимаются; то, что успело добавиться за время обмена, остаётся до следующего.
 *
 * Ключи общие с Android: часы пишут журнал на телефон мимо веба, и Kotlin ставит те же метки.
 * Первый обмен после обновления — полный: до него корзины не было, и считать, что всё уже
 * на сервере, нельзя.
 */
const KEY = {
  events: 'sync_pending_events',
  windows: 'sync_pending_windows',
  /** Метка «с момента» для ответа сервера — его же часы (serverNow прошлого ответа). */
  since: 'sync_since',
  /** Когда собирали прошлую удачную посылку, по часам устройства: по ней едут заметки. */
  sentAt: 'sync_sent_at',
  /** Полная посылка уже уезжала: дальше только помеченное. */
  mode: 'sync_incremental',
} as const

async function список(storage: Storage, key: string): Promise<number[]> {
  const raw = await storage.get(key)
  if (raw === null) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === 'number') : []
  } catch {
    return []
  }
}

async function записать(storage: Storage, key: string, items: number[]): Promise<void> {
  if (items.length === 0) await storage.remove(key)
  else await storage.set(key, JSON.stringify(items))
}

export async function rememberEvent(storage: Storage, id: number): Promise<void> {
  const есть = await список(storage, KEY.events)
  if (!есть.includes(id)) await записать(storage, KEY.events, [...есть, id])
}

export async function rememberWindow(storage: Storage, start: number): Promise<void> {
  const есть = await список(storage, KEY.windows)
  if (!есть.includes(start)) await записать(storage, KEY.windows, [...есть, start])
}

export interface Outbox {
  /** Полная посылка уже была: слать только помеченное. */
  incremental: boolean
  events: Set<number>
  windows: Set<number>
  sentAt: number
}

export async function outboxOf(storage: Storage): Promise<Outbox> {
  return {
    incremental: (await storage.get(KEY.mode)) === '1',
    events: new Set(await список(storage, KEY.events)),
    windows: new Set(await список(storage, KEY.windows)),
    sentAt: Number((await storage.get(KEY.sentAt)) ?? 0),
  }
}

/** С какого момента по часам сервера просить ответ; 0 — всё. */
export async function sinceOf(storage: Storage): Promise<number> {
  return Number((await storage.get(KEY.since)) ?? 0)
}

/** Посылка дошла: снять метки с того, что в ней было, запомнить момент и часы сервера. */
export async function markSent(storage: Storage, sent: { events: number[]; windows: number[] }, serverNow: number, sentAt: number): Promise<void> {
  const события = new Set(sent.events)
  const окна = new Set(sent.windows)
  await записать(storage, KEY.events, (await список(storage, KEY.events)).filter((id) => !события.has(id)))
  await записать(storage, KEY.windows, (await список(storage, KEY.windows)).filter((s) => !окна.has(s)))
  await storage.set(KEY.since, String(serverNow))
  await storage.set(KEY.sentAt, String(sentAt))
  await storage.set(KEY.mode, '1')
}
